import { ChangeDetectorRef, Component, Host, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MenuService } from './app.menu.service';
import { LayoutService } from './service/app.layout.service';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: '[app-menuitem]',
    template: `
		<ng-container>
            <!-- Título clickeable para items root con submenús -->
            <div *ngIf="root && item.items && item.visible !== false" 
                 class="layout-menuitem-root-text layout-menuitem-root-clickable"
                 (click)="itemClick($event)"
                 (keydown.enter)="itemClick($event)"
                 (keydown.space)="itemClick($event)"
                 [attr.tabindex]="0"
                 [attr.role]="'button'"
                 [attr.aria-expanded]="active"
                 [attr.aria-label]="item.label">
                <i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
                <span>{{item.label}}</span>
                <i [ngClass]="active ? 'pi pi-fw pi-angle-down' : 'pi pi-fw pi-angle-right'" 
                   class="layout-submenu-toggler">
                </i>
            </div>
            <!-- Título simple para items root sin submenús -->
            <div *ngIf="root && !item.items && item.visible !== false" 
                 class="layout-menuitem-root-text">
                {{item.label}}
            </div>
			<!-- Enlace para items NO root con submenús o sin routerLink -->
			<a *ngIf="!root && (item.items || !item.routerLink) && item.visible !== false" 
			   [attr.href]="item.url || '#'" 
			   (click)="itemClick($event)"
			   [ngClass]="item.class" 
			   [attr.target]="item.target" 
			   tabindex="0" 
			   pRipple>
				<i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
				<span class="layout-menuitem-text">{{item.label}}</span>
				<i [ngClass]="active ? 'pi pi-fw pi-angle-down' : 'pi pi-fw pi-angle-right'" 
				   class="layout-submenu-toggler" 
				   *ngIf="item.items">
				</i>
			</a>
			<!-- Enlace para items sin submenús con routerLink -->
			<a *ngIf="item.routerLink && !item.items && item.visible !== false" 
			   (click)="itemClick($event)" 
			   [ngClass]="item.class" 
			   [routerLink]="item.routerLink" 
			   routerLinkActive="active-route" 
			   [routerLinkActiveOptions]="item.routerLinkActiveOptions||{ paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' }"
               [fragment]="item.fragment" 
			   [queryParamsHandling]="item.queryParamsHandling" 
			   [preserveFragment]="item.preserveFragment" 
			   [skipLocationChange]="item.skipLocationChange" 
			   [replaceUrl]="item.replaceUrl" 
			   [state]="item.state" 
			   [queryParams]="item.queryParams"
               [attr.target]="item.target" 
			   tabindex="0" 
			   pRipple>
				<i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
				<span class="layout-menuitem-text">{{item.label}}</span>
			</a>

			<ul *ngIf="item.items && item.visible !== false" 
				[@children]="submenuAnimation"
				[class.menu-collapsed]="!active"
				[class.menu-expanded]="active"
				style="overflow: hidden;">
				<ng-template ngFor let-child let-i="index" [ngForOf]="item.items">
					<li app-menuitem [item]="child" [index]="i" [parentKey]="key" [class]="child.badgeClass"></li>
				</ng-template>
			</ul>
		</ng-container>
    `,
    animations: [
        trigger('children', [
            state('collapsed', style({
                height: '0px',
                overflow: 'hidden',
                opacity: 0,
                margin: '0',
                padding: '0'
            })),
            state('expanded', style({
                height: '*',
                overflow: 'visible',
                opacity: 1
            })),
            transition('collapsed => expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
            transition('expanded => collapsed', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
        ])
    ]
})
export class AppMenuitemComponent implements OnInit, OnDestroy {

    @Input() item: any;

    @Input() index!: number;

    @Input() @HostBinding('class.layout-root-menuitem') root!: boolean;

    @Input() parentKey!: string;

    active: boolean = false;

    menuSourceSubscription: Subscription;

    menuResetSubscription: Subscription;

    key: string = "";

    constructor(public layoutService: LayoutService, private cd: ChangeDetectorRef, public router: Router, private menuService: MenuService) {
        this.menuSourceSubscription = this.menuService.menuSource$.subscribe(value => {
            Promise.resolve(null).then(() => {
                if (value.routeEvent) {
                    this.active = (value.key === this.key || value.key.startsWith(this.key + '-')) ? true : false;
                }
                else {
                    // Si el key coincide exactamente, mantener el estado actual (ya fue toggleado en itemClick)
                    // Si es diferente, cerrar este menú
                    if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
                        this.active = false;
                    }
                    // Si el key coincide, no hacer nada (el estado ya fue toggleado en itemClick)
                }
                this.cd.markForCheck();
            });
        });

        this.menuResetSubscription = this.menuService.resetSource$.subscribe(() => {
            this.active = false;
        });

        this.router.events.pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(params => {
                if (this.item.routerLink) {
                    this.updateActiveStateFromRoute();
                }
            });
    }

    ngOnInit() {
        this.key = this.parentKey ? this.parentKey + '-' + this.index : String(this.index);

        // CRÍTICO: Asegurar que los menús root inicien SIEMPRE cerrados
        if (this.root && this.item.items) {
            this.active = false;
            // Forzar detección inmediata para aplicar el estado cerrado
            this.cd.detectChanges();
        }

        // Solo verificar rutas activas para items con routerLink (no para items padre con submenús)
        if (this.item.routerLink && !this.item.items) {
            this.updateActiveStateFromRoute();
        } else if (this.item.items && this.root) {
            // Para items root con submenús, verificar si algún hijo tiene una ruta activa
            // Usar setTimeout para asegurar que el router esté completamente inicializado
            // PERO solo si realmente hay una ruta activa
            setTimeout(() => {
                // Solo verificar si la ruta actual coincide con algún hijo
                const currentUrl = this.router.url;
                const hasActiveChild = this.item.items.some((child: any) => {
                    if (child.routerLink && child.visible !== false) {
                        const route = Array.isArray(child.routerLink) ? child.routerLink[0] : child.routerLink;
                        return currentUrl.startsWith(route);
                    }
                    return false;
                });
                
                // Solo abrir si hay una ruta activa
                if (hasActiveChild) {
                    this.active = true;
                    this.menuService.onMenuStateChange({ key: this.key, routeEvent: true });
                    this.cd.detectChanges();
                }
            }, 500);
        }
    }

    updateActiveStateFromRoute() {
        let activeRoute = this.router.isActive(this.item.routerLink[0], { paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' });

        if (activeRoute) {
            this.menuService.onMenuStateChange({ key: this.key, routeEvent: true });
        }
    }


    itemClick(event: Event) {
        // avoid processing disabled items
        if (this.item.disabled) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        // execute command
        if (this.item.command) {
            this.item.command({ originalEvent: event, item: this.item });
        }

        // toggle active state for items with submenus
        if (this.item.items) {
            event.preventDefault(); // Prevent navigation for parent items
            event.stopPropagation(); // Prevent event bubbling
            this.active = !this.active;
            this.cd.detectChanges(); // Forzar detección de cambios
            this.menuService.onMenuStateChange({ key: this.key });
        } else if (this.item.routerLink) {
            // For items without submenus, just navigate
            this.menuService.onMenuStateChange({ key: this.key });
        }
    }

    get submenuAnimation() {
        return this.active ? 'expanded' : 'collapsed';
    }

    @HostBinding('class.active-menuitem') 
    get activeClass() {
        return this.active && !this.root;
    }

    ngOnDestroy() {
        if (this.menuSourceSubscription) {
            this.menuSourceSubscription.unsubscribe();
        }

        if (this.menuResetSubscription) {
            this.menuResetSubscription.unsubscribe();
        }
    }
}
